import React, {Suspense, useEffect, useState} from 'react';
import {PageContainer, ProCard, ProForm, ProFormDateRangePicker, ProFormSelect, ProFormSwitch,} from '@ant-design/pro-components';
import {Alert, Button, Form, Modal, Result, Space, Spin, Typography, message} from 'antd';
import {FileExcelOutlined, FilePptOutlined} from '@ant-design/icons';
import {useAtom, useAtomValue} from 'jotai';
import dayjs from 'dayjs';
import {accountsAtom, isGeneratingAtom, reportResultAtom, selectedAccountAtom, selectedStartDateAtom, selectedEndDateAtom} from './atoms';
import axiosApiInstance from "../../../api/axiosClient";

const AccountSelect = () => {
    const [selectedAccount, setSelectedAccount] = useAtom(selectedAccountAtom);
    const {data: accounts} = useAtomValue(accountsAtom);

    return (
        <ProFormSelect
            name="account"
            label="TikTok Account"
            rules={[{required: true, message: 'Please select an account'}]}
            options={accounts?.map(account => ({
                label: account.label,
                value: account.value
            })) || []}
            placeholder="Select TikTok account"
            fieldProps={{
                value: selectedAccount,
                onChange: (value) => setSelectedAccount(value),
            }}
        />
    );
};

const LoadingAccountSelect = () => (
    <ProFormSelect
        name="account"
        label="TikTok Account"
        placeholder="Loading accounts..."
        fieldProps={{loading: true}}
    />
);

const TikTokReports: React.FC = () => {
    const [selectedAccount] = useAtom(selectedAccountAtom);
    const [selectedStartDate, setSelectedStartDate] = useAtom(selectedStartDateAtom);
    const [selectedEndDate, setSelectedEndDate] = useAtom(selectedEndDateAtom);
    const [isGenerating, setIsGenerating] = useAtom(isGeneratingAtom);
    const [reportResult, setReportResult] = useAtom(reportResultAtom);
    const [isAnnualMode, setIsAnnualMode] = useState(false);
    const [competitorsExcelOnly, setCompetitorsExcelOnly] = useState(false);
    const [form] = Form.useForm<any>();

    useEffect(() => {
        form.setFieldsValue({
            account: selectedAccount,
            dateRange: [dayjs(selectedStartDate), dayjs(selectedEndDate)],
        });
    }, []);

    const buildRequestPayload = () => {
        const start = dayjs(selectedStartDate);
        const end = competitorsExcelOnly ? dayjs(selectedEndDate) : (isAnnualMode ? dayjs(selectedEndDate) : dayjs(selectedStartDate));
        return {
            username: selectedAccount,
            month: start.month() + 1,
            year: start.year(),
            endMonth: end.month() + 1,
            endYear: end.year(),
            isAnnual: competitorsExcelOnly ? false : isAnnualMode,
        };
    };

    const downloadCompetitorExcel = async () => {
        try {
            setIsGenerating(true);
            const payload = buildRequestPayload();
            const response = await axiosApiInstance.post('/api/tiktok-reports/export-competitors-excel', payload, {
                responseType: 'blob',
                timeout: 10 * 60 * 1000,
            });
            const header = String(response.headers['content-disposition'] ?? '');
            const match = header.match(/filename="([^"]+)"/);
            const filename = match?.[1] || `${payload.username}_competitors.xlsx`;
            const url = URL.createObjectURL(response.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.click();
            URL.revokeObjectURL(url);
            message.success('Competitor benchmark Excel downloaded');
        } catch (error) {
            console.error(error);
            message.error('Failed to download competitor Excel');
        } finally {
            setIsGenerating(false);
        }
    };

    const generateReport = async () => {
        try {
            setIsGenerating(true);
            const response = await axiosApiInstance.post('/api/tiktok-reports/generate', buildRequestPayload());
            setIsGenerating(false);
            setReportResult(response.data);
        } catch (error) {
            setIsGenerating(false);
            console.error(error);
            setReportResult({reports: []});
        }
    };

    const onFinish = async () => {
        if (!selectedAccount || !selectedStartDate || !selectedEndDate) return;
        if (competitorsExcelOnly) {
            await downloadCompetitorExcel();
            return;
        }
        await generateReport();
    };

    return (
        <PageContainer>
            <ProCard>
                <Alert
                    message="Generate TikTok Performance Reports"
                    description="Use Generate Reports for monthly Slides output, or Generate Annual Report for a single deck across the range. Turn on “Competitors Excel only” to download brand + competitor benchmark stats as Excel instead of creating a presentation."
                    type="info"
                    showIcon
                    style={{marginBottom: 24}}
                />

                <Spin spinning={isGenerating}>
                    <ProForm
                        onFinish={onFinish}
                        form={form}
                        submitter={{
                            render: (props) => {
                                if (competitorsExcelOnly) {
                                    return [
                                        <Button
                                            key="excel"
                                            type="primary"
                                            size="large"
                                            icon={<FileExcelOutlined/>}
                                            loading={isGenerating}
                                            onClick={() => props.form?.submit()}
                                        >
                                            Download competitors Excel
                                        </Button>,
                                    ];
                                }
                                return [
                                    <Space key="actions">
                                        <Button
                                            type="primary"
                                            size="large"
                                            loading={isGenerating}
                                            onClick={() => {
                                                setIsAnnualMode(false);
                                                props.form?.submit();
                                            }}
                                        >
                                            Generate Monthly
                                        </Button>
                                        <Button
                                            size="large"
                                            loading={isGenerating}
                                            onClick={() => {
                                                setIsAnnualMode(true);
                                                props.form?.submit();
                                            }}
                                        >
                                            Generate Annual
                                        </Button>
                                    </Space>,
                                ];
                            },
                        }}
                    >
                        <Suspense fallback={<LoadingAccountSelect/>}>
                            <AccountSelect/>
                        </Suspense>

                        <ProFormSwitch
                            name="competitorsExcelOnly"
                            label="Competitors Excel only"
                            tooltip="Download an Excel file with our brand and configured competitors (profile benchmarks). Includes a second sheet with TikTok period metrics for our brand. No Google Slides report is created."
                            fieldProps={{
                                checked: competitorsExcelOnly,
                                onChange: (checked) => setCompetitorsExcelOnly(checked),
                            }}
                        />

                        <ProFormDateRangePicker
                            name="dateRange"
                            label="Report Period"
                            rules={[
                                {required: true, message: 'Please select a date range'},
                                {
                                    validator: (_, value) => {
                                        if (!value?.[0] || !value?.[1]) return Promise.resolve();
                                        if (value[1].diff(value[0], 'month') > 11) {
                                            return Promise.reject(new Error('Range cannot exceed 12 months'));
                                        }
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                            fieldProps={{
                                picker: 'month' as any,
                                onChange: (values: any) => {
                                    if (values?.[0]) setSelectedStartDate(values[0].format('YYYY-MM'));
                                    if (values?.[1]) setSelectedEndDate(values[1].format('YYYY-MM'));
                                },
                                disabledDate: (current) => {
                                    return current && current > dayjs().subtract(2, 'day');
                                },
                            }}
                        />
                    </ProForm>
                </Spin>

                <Modal
                    open={!!reportResult?.reports?.length}
                    onOk={() => setReportResult({reports: []})}
                    closable={false}
                    maskClosable={true}
                    cancelButtonProps={{hidden: true}}
                    width={580}
                >
                    <Result
                        status="success"
                        title="Reports Generated Successfully!"
                        subTitle={
                            <Typography.Text type="secondary">
                                {reportResult?.reports?.length === 1
                                    ? '1 report has been generated and is ready to view.'
                                    : `${reportResult?.reports?.length} reports have been generated and are ready to view.`}
                            </Typography.Text>
                        }
                        extra={
                            <div style={{textAlign: 'left'}}>
                                {reportResult?.reports?.map((report) => {
                                    const label = dayjs(`${report.year}-${String(report.month).padStart(2, '0')}-01`).format('MMMM YYYY');
                                    return (
                                        <div key={`${report.year}-${report.month}`} style={{marginBottom: 12}}>
                                            <Typography.Text strong style={{display: 'block', marginBottom: 6}}>
                                                {label}
                                            </Typography.Text>
                                            <Button
                                                type="primary"
                                                icon={<FilePptOutlined/>}
                                                href={report.presentationUrl}
                                                target="_blank"
                                                style={{marginRight: 8}}
                                            >
                                                Presentation
                                            </Button>
                                            <Button
                                                icon={<FileExcelOutlined/>}
                                                href={report.spreadsheetUrl}
                                                target="_blank"
                                            >
                                                Raw Data
                                            </Button>
                                        </div>
                                    );
                                })}
                            </div>
                        }
                    />
                </Modal>
            </ProCard>
        </PageContainer>
    );
};

export default TikTokReports;